import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { noop } from 'lodash'
import { FormModal } from 'transfix-ui/components'
import { Button } from 'transfix-ui/components/Button'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import Box from 'transfix-ui/core/Box'
import {
  requestCarrierApproval,
  createVendor,
  updateVendor,
  getCarrierChecklist,
} from 'utils/actions/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { headerActionsSelector } from 'apps/carrier/utils/selectors/headerActions'
import { useDeleteBCA } from 'apps/marketplace/apiTypes/carriers'
import { AddTrailer } from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer'
import { useModal } from 'utils/hooks'
import { useTrailerManagementContext } from '../CarrierTrailers/TrailerManagementContext'
import { useCarrierTrailersLazyQuery } from '../CarrierTrailers/graphql/CarrierTrailers'
import { getCarrierTrailersVariables } from '../CarrierTrailers/hooks/useCarrierTrailers'
import { useCarrierStateQuery } from '../Compliance/Cards/CarrierStatus/graphql/CarrierState'
import { useTransfixRulesAssessmentQuery } from '../Compliance/Cards/CarrierStatus/graphql/TransfixRulesAssessment'

export function HeaderActions() {
  const { openToast } = useGlobalToast()
  const dispatch = useDispatch()
  const { userPermissions } = useUserDetails()

  const {
    carrierActivated,
    carrierDocumentsCheckLists,
    existsInNetsuite,
    id: carrierApplicationId,
    inProgress,
  } = useSelector(headerActionsSelector)
  const {
    canDeleteCarrierBca,
    pushToNetsuite: canPushToNetsuite,
    requestCarrierApproval: permitted,
  } = userPermissions || {}
  const canRequestApproval = permitted && carrierActivated
  const { checklists } = carrierDocumentsCheckLists || {}
  const { brokerCarrierAgreement } = checklists || {}

  const requestApproval = () => {
    if (carrierApplicationId) {
      dispatch(requestCarrierApproval(carrierApplicationId))
    }
  }
  const vendorClickHandler = () => {
    if (carrierApplicationId) {
      dispatch(
        existsInNetsuite ? updateVendor(carrierApplicationId) : createVendor(carrierApplicationId)
      )
    }
  }
  const history = useHistory()
  const [showDeleteBca, setShowDeleteBca] = useState(false)
  const { error, loading, mutate: callDeleteBCA } = useDeleteBCA({
    id: String(carrierApplicationId),
  })
  const isHideDeleteBcaButtonEnabled = useFeatureFlag('hideDeleteBcaButton')
  const shouldShowDeleteBCAButton =
    brokerCarrierAgreement && canDeleteCarrierBca && !isHideDeleteBcaButtonEnabled

  const { refetch: stateRefetch } =
    useCarrierStateQuery({ variables: { carrierApplicationId: `${carrierApplicationId}` } }) || {}

  const { refetch: transfixRulesRefetch } = useTransfixRulesAssessmentQuery({
    variables: { carrierApplicationId: String(carrierApplicationId) },
  })

  const deleteBCA = async () => {
    try {
      await callDeleteBCA()
      if (carrierApplicationId) {
        openToast({
          toastMessage: 'Successfully deleted BCA.',
          toastType: 'default',
        })
        dispatch(getCarrierChecklist(carrierApplicationId))
      }
      setShowDeleteBca(false)
      stateRefetch()
      transfixRulesRefetch()
    } catch {
      noop()
    }
  }

  const { currentFilters } = useTrailerManagementContext()
  const { closeModal, isOpen, openModal } = useModal()
  const { id: carrierId = '', tab = '' } = useParams<{ id: string; tab: string }>()

  const dropTrailerEnabled = useFeatureFlag('carrierTrailerManagement')

  const [refresh] = useCarrierTrailersLazyQuery({
    variables: getCarrierTrailersVariables(currentFilters, carrierId),
    fetchPolicy: 'network-only',
  })

  const successHandler = () => {
    refresh()
  }

  return (
    <Box display='flex' flexWrap='wrap' gap='4px' justifyContent='end'>
      {shouldShowDeleteBCAButton && (
        <Button color='secondary' onClick={() => setShowDeleteBca(true)}>
          Delete BCA
        </Button>
      )}
      {canPushToNetsuite && (
        <Button
          color='secondary'
          onClick={vendorClickHandler}
          data-test='push-to-netsuite'
          disabled={inProgress}>
          {existsInNetsuite ? 'Update' : 'Create'} Vendor
        </Button>
      )}
      {canRequestApproval && (
        <Button
          color='secondary'
          onClick={requestApproval}
          data-test='request-compliance-approval'
          disabled={inProgress}>
          Request Compliance Approval
        </Button>
      )}
      {tab === 'trailers' && dropTrailerEnabled && (
        <Button color='secondary' onClick={openModal('trailer-modal')}>
          Add Trailer
        </Button>
      )}
      <Button color='primary' onClick={() => history.push(`/edit/${carrierApplicationId}`)}>
        Update Active Shipments
      </Button>
      {showDeleteBca && (
        <div className='tl'>
          <FormModal
            title='Are you sure?'
            size={4}
            open={showDeleteBca}
            actions={[
              { label: 'Cancel', type: 'dismiss', disabled: loading },
              {
                label: 'Delete',
                type: 'confirm',
                disabled: loading,
                action: deleteBCA,
              },
            ]}
            onClose={() => setShowDeleteBca(false)}
            dataTest='delete-bca'
            error={error?.message || ''}>
            The BCA for this carrier will be removed.
          </FormModal>
        </div>
      )}
      {dropTrailerEnabled && isOpen('trailer-modal') && (
        <AddTrailer
          onClose={closeModal}
          open
          carrierApplicationId={carrierId}
          onSuccess={successHandler}
          showEmptyAtDelivery
          showInactive
        />
      )}
    </Box>
  )
}
